import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CHAT_MODELS, type ChatModel } from '@couch/shared';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { api, errorMessage } from '../../api/client';
import { ConnectClaudeHint } from '../../components/ConnectClaudeHint';
import { MACHINES_KEY } from '../machines/MachineActions';

export const CHATS_KEY = ['chats'] as const;
export const MODEL_LABELS: Readonly<Record<ChatModel, string>> = { 'claude-sonnet-5': 'Claude Sonnet 5 (fast, cheaper)', 'claude-opus-5-5': 'Claude Opus 5.5 (strongest)' };
const when = (iso: string) => new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });

function NewChat() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const machines = useQuery({ queryKey: MACHINES_KEY, queryFn: api.listMachines });
  const tokens = useQuery({ queryKey: ['provider-tokens'], queryFn: api.providerTokens });
  const online = (machines.data?.machines ?? []).filter((m) => m.online);
  const [machineOverride, setMachineOverride] = useState('');
  const [model, setModel] = useState<ChatModel>('claude-sonnet-5');
  const machineId = machineOverride || online[0]?.id || '';
  const hasKey = tokens.data?.tokens.some((t) => t.provider === 'anthropic' && t.set) ?? true;
  const create = useMutation({
    mutationFn: () => api.createChat({ machineId, model }),
    onSuccess: async ({ chat }) => { await queryClient.invalidateQueries({ queryKey: CHATS_KEY }); await navigate(`/claude/${chat.id}`); },
  });
  return (
    <form className="panel" onSubmit={(e) => { e.preventDefault(); create.mutate(); }}>
      <h2>New chat</h2>
      {!hasKey && <p className="notice">Claude here runs on your own Anthropic API key — add it in <Link to="/account">Account → Model sources</Link>.</p>}
      {machines.isSuccess && online.length === 0 && <p className="notice">No machine is online. Switch one on to start.</p>}
      <div className="recipe-pickers">
        <label>Machine{' '}
          <select value={machineId} onChange={(e) => { setMachineOverride(e.target.value); }}>
            {online.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
        </label>
        <label>Model{' '}
          <select value={model} onChange={(e) => { setModel(e.target.value as ChatModel); }}>
            {CHAT_MODELS.map((m) => <option key={m} value={m}>{MODEL_LABELS[m]}</option>)}
          </select>
        </label>
      </div>
      {create.isError && <p role="alert" className="form-error">{errorMessage(create.error)}</p>}
      <p><button type="submit" disabled={!machineId || !hasKey || create.isPending}>Start</button></p>
    </form>
  );
}

/** The Claude tab (doc 38, renamed in doc 41): pictures + a prompt → Claude builds the ComfyUI workflow (doc 38). */
export function WorkflowPage() {
  const chats = useQuery({ queryKey: CHATS_KEY, queryFn: api.listChats });
  const list = chats.data?.chats ?? [];
  return (
    <section aria-labelledby="workflow-title">
      <h1 id="workflow-title">Claude</h1>
      <p className="muted">Give Claude pictures and say what you want — “change her pose to sitting”, “make this a short video”. It builds the ComfyUI workflow on your machine, runs it and saves it under Workflows.</p>
      <NewChat />
      {chats.isError && <p role="alert" className="form-error">{errorMessage(chats.error)}</p>}
      {list.length > 0 && (
        <ul className="chat-list">
          {list.map((c) => (
            <li key={c.id}>
              <Link to={`/claude/${c.id}`}>{c.title}</Link>
              <span className="muted"> · {when(c.updatedAt)}{c.status === 'running' ? ' · working…' : c.status === 'failed' ? ' · stopped with an error' : ''}</span>
            </li>
          ))}
        </ul>
      )}
      <ConnectClaudeHint />
    </section>
  );
}
